import React from 'react';

/**
 * Hook to use stepper controls, keeping state of current step, completed steps, etc.
 */
export function useStepper(props: {
    /** Index of the initial step */
    initialStep?: number;
    /** Number of steps stepper has.  */
    stepsCount: number;
}): {
    /** Set of indices of completed steps. */
    completedSteps: Set<number>;

    /** Index of the current step. */
    currentStepIndex: number;

    /** Callback to reduce the current step index. */
    onStepBack: () => void;

    /** Callback to go step forward. When step is completed adds the index to completed steps set as well. */
    onStepForward: (stepCompleted?: boolean) => void;

    /** Callback to reset to initial state. */
    onRestart: () => void;
} {
    const { stepsCount } = props;
    const [currentStepIndex, setCurrentStepIndex] = React.useState(props.initialStep ?? 0);
    const [completedSteps, setCompletedSteps] = React.useState(new Set<number>());

    const onStepForward = (stepCompleted = true) => {
        if (stepCompleted) {
            setCompletedSteps((prev) => new Set([...Array.from(prev), currentStepIndex]));
        }
        setCurrentStepIndex((currentStep) =>
            // If we're on last step just complete that step and keep the currentStepIndex on the last step.
            currentStep === stepsCount - 1 ? currentStep : currentStep + 1
        );
    };

    const onStepBack = () => {
        setCurrentStepIndex((prev) => (prev > 0 ? prev - 1 : 0));
    };

    const onRestart = () => {
        setCompletedSteps(new Set<number>());
        setCurrentStepIndex(0);
    };

    return { completedSteps, currentStepIndex, onStepForward, onStepBack, onRestart };
}
